"use client";
import React, { useMemo, useEffect, useState } from "react";
import { motion } from "framer-motion";
import ProductCard from "./ProductCard";

const ProductGrid = ({ products = [], totalItemsCount = 0 }) => {
  // --- Anim variants
  const containerVariants = useMemo(
    () => ({
      hidden: { opacity: 0 },
      visible: {
        opacity: 1,
        transition: { staggerChildren: 0.06, delayChildren: 0.1 },
      },
    }),
    []
  );

  const itemVariants = useMemo(
    () => ({
      hidden: { opacity: 0, y: 30, scale: 0.96 },
      visible: {
        opacity: 1,
        y: 0,
        scale: 1,
        transition: { duration: 0.45, ease: "easeOut" },
      },
    }),
    []
  );

  // --- Safely compute a fall distance for background emojis (no SSR crash)
  const [fallDistance, setFallDistance] = useState(900);
  useEffect(() => {
    if (typeof window !== "undefined") {
      setFallDistance(window.innerHeight + 140);
    }
  }, []);

  // Background floating/ falling icons
  const fallingIcons = [
    "🧁",
    "🍰",
    "🍪",
    "🥧",
    "🍩",
    "🎂",
    "🍓",
    "🍫",
    "🥜",
    "🍯",
    "🧁",
    "🍰",
    "🍪",
    "🥧",
    "🍩",
    "🎂",
  ];

  return (
    <motion.section
      className="relative px-0 py-0 bg-white/80 backdrop-blur-sm shadow-2xl rounded-3xl border-2 border-orange-200 w-full overflow-hidden"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45 }}
    >
      {/* Count badge */}
      {/* <div className="absolute top-5 right-5 z-20">
        <div className="text-sm md:text-base text-cyan-900 bg-red-100 px-4 py-2 rounded-full font-semibold shadow-lg border border-red-200">
          {totalItemsCount} delicious items
        </div>
      </div> */}

      {/* Subtle background decor */}
      <div className="absolute inset-0 pointer-events-none">
        {fallingIcons.map((icon, idx) => (
          <motion.div
            key={`fall-${idx}`}
            className="absolute text-2xl opacity-20"
            style={{ left: `${(idx * 6) % 100}%`, top: -50 }}
            animate={{
              y: [0, fallDistance],
              rotate: [0, 360],
              opacity: [0, 0.35, 0.25, 0],
            }}
            transition={{
              duration: 7 + (idx % 5),
              repeat: Infinity,
              delay: idx * 0.25,
              ease: "linear",
            }}
          >
            {icon}
          </motion.div>
        ))}

        <motion.div
          className="absolute top-10 right-10 w-28 h-28 bg-gradient-to-br from-red-100 to-orange-100 rounded-full opacity-20"
          animate={{ scale: [1, 1.15, 1], rotate: [0, 180, 360] }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        />
        <motion.div
          className="absolute bottom-10 left-10 w-24 h-24 bg-gradient-to-br from-orange-100 to-yellow-100 rounded-full opacity-20"
          animate={{ scale: [1, 1.2, 1], rotate: [360, 180, 0] }}
          transition={{ duration: 24, repeat: Infinity, ease: "linear" }}
        />
      </div>

      {/* GRID WRAPPER — always rendered to avoid layout jump */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="relative z-10 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6 p-6 auto-rows-fr"
      >
        {products.length > 0 ? (
          products.map((product, index) => (
            <motion.div
              key={product.id ?? product.name ?? index}
              variants={itemVariants}
              className="w-full h-full flex"
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
            >
              {/* Make the card stretch full height to align rows */}
              <div className="w-full h-full flex">
                <ProductCard product={product} fit="cover" ratio="square" />
              </div>
            </motion.div>
          ))
        ) : (
          // EMPTY STATE — occupies full grid width to keep layout stable
          <motion.div
            className="col-span-1 sm:col-span-2 xl:col-span-3 min-h-[320px] flex flex-col items-center justify-center bg-white/60 rounded-3xl border border-orange-200 shadow-sm p-8 text-center"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.35 }}
          >
            <motion.div
              className="text-7xl md:text-8xl mb-5"
              animate={{ rotate: [0, -10, 10, 0], scale: [1, 1.08, 1] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            >
              🧁
            </motion.div>
            <h3
              className="text-2xl md:text-3xl font-bold text-cyan-900 mb-2"
              style={{ fontFamily: "'Sansita Swashed', cursive" }}
            >
              No items match your filters
            </h3>
            <p className="text-slate-600 max-w-xl">
              Try adjusting category or price range to see more items.
            </p>

            <div className="flex flex-wrap gap-3 mt-6 justify-center">
              {["Tea & Snacks", "Milkshakes", "Cakes (1 KG)"].map((s, i) => (
                <motion.span
                  key={s}
                  className="px-5 py-2.5 bg-gradient-to-r from-red-100 to-orange-100 text-red-600 rounded-full text-sm md:text-base font-medium shadow hover:from-red-200 hover:to-orange-200 cursor-default"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.25, delay: 0.15 * i }}
                >
                  {s}
                </motion.span>
              ))}
            </div>
          </motion.div>
        )}
      </motion.div>
    </motion.section>
  );
};

export default ProductGrid;
